import {
  IonButton,
  IonContent,
  IonIcon,
  IonInput,
  IonPage,
} from '@ionic/react';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { IState } from 'appRedux/createStore';
import { Auth } from 'aws-amplify';
import { eyeOutline, eyeOffOutline, lockClosedOutline, warningOutline } from 'ionicons/icons';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import CasinoNavBar from 'components/CasinoNavBar';
import CountdownTimer from 'components/CountdownTimer/CountdownTimer';
import useToast from 'common/reactHooks/useToast';
import { getResetPasswordErrorMessage } from 'common/common';
import { onPromise } from '../CreateAccount';

interface FormData {
  otp_number: string;
  password: string;
  passwordConfirmation: string;
}

const schema = yup.object().shape({
  otp_number: yup
    .string()
    .required('Verification code is required')
    .matches(/^[0-9]+$/, 'Must be only digits')
    .min(6, 'Must be exactly 6 digits')
    .max(6, 'Must be exactly 6 digits'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must include at least 8 characters')
    .max(24, 'Please use a shorter password (max: 24 characters)'),
  passwordConfirmation: yup.string().oneOf([yup.ref('password'), null], 'Passwords must match'),
});

export const EnterpriseResetPassword = () => {
  const history = useHistory();
  const showToast = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isTimerExpired, setIsTimerExpired] = useState(false);

  const email = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.user
  ) as string;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: yupResolver(schema) });

  const onResendCode = () => {
    if (!email) return;
    Auth.forgotPassword(email)
      .then(() => showToast({ message: 'New code has been sent' }))
      .catch((err) => showToast({ message: err.message, type: 'danger' }));
    setIsTimerExpired(false);
  };

  const onSubmit = async (data: FormData) => {
    if (!email) {
      showToast({ message: 'Email is required', type: 'danger' });
      return;
    }
    await Auth.forgotPasswordSubmit(email, data.otp_number, data.password)
      .then(() => {
        showToast({ message: 'Your password has been changed' });
        history.push('/auth/casinoadmin');
      })
      .catch((e) => {
        showToast({ type: 'danger', message: getResetPasswordErrorMessage(e?.message) });
      });
  };

  return (
    <IonPage className="login-v2-page">
      <IonContent fullscreen>
        <div className="login-v2-wrapper">
          <div className="casino-fixed-top__nav login-v2-nav-wrap">
            <CasinoNavBar hideSearch hideLogo hideMenu />
            <button
              className="login-v2-back-btn"
              onClick={() => history.goBack()}
              aria-label="Go back"
            />
          </div>
          <div className="login-v2-bg">
            <div className="login-v2-body">
              <h1 className="login-v2-welcome">RESET PASSWORD</h1>
              <div className="login-v2-form">
                <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem', textAlign: 'center', margin: '0 0 20px' }}>
                  We sent a 6-digit code to{' '}
                  <span style={{ color: '#d4a030', fontWeight: 600 }}>{email}</span>
                </p>

                <form onSubmit={onPromise(handleSubmit((data) => void onSubmit(data)))}>
                  {/* OTP field */}
                  <div className="login-v2-field-wrap login-v2-pass-wrap">
                    <div className="login-v2-pass-content">
                      <Controller
                        name="otp_number"
                        control={control}
                        defaultValue=""
                        render={({ field: { onChange } }) => (
                          <IonInput
                            type="text"
                            inputmode="numeric"
                            maxlength={6}
                            placeholder="6-digit code"
                            onIonChange={onChange}
                          />
                        )}
                        rules={{ required: true }}
                      />
                    </div>
                    <div className="login-v2-eye-btn">
                      {isTimerExpired ? (
                        <button
                          type="button"
                          className="resend-otp-btn"
                          onClick={onResendCode}
                        >
                          Resend
                        </button>
                      ) : (
                        <CountdownTimer minutes="1" setResendTimerExpired={setIsTimerExpired} />
                      )}
                    </div>
                  </div>
                  {errors.otp_number?.message && (
                    <div className="login-v2-field-error">
                      <IonIcon icon={warningOutline} />
                      <span>{errors.otp_number.message}</span>
                    </div>
                  )}

                  {/* New password field */}
                  <div className="login-v2-field-wrap login-v2-pass-wrap">
                    <IonIcon icon={lockClosedOutline} className="login-v2-field-prefix-icon" />
                    <div className="login-v2-pass-content">
                      <Controller
                        name="password"
                        control={control}
                        defaultValue=""
                        render={({ field: { onChange } }) => (
                          <IonInput
                            type={showPassword ? 'text' : 'password'}
                            placeholder="New password"
                            onIonChange={onChange}
                            autocomplete="new-password"
                          />
                        )}
                        rules={{ required: true }}
                      />
                    </div>
                    <div className="login-v2-eye-btn" onClick={() => setShowPassword((v) => !v)}>
                      <IonIcon icon={showPassword ? eyeOffOutline : eyeOutline} />
                    </div>
                  </div>
                  {errors.password?.message && (
                    <div className="login-v2-field-error">
                      <IonIcon icon={warningOutline} />
                      <span>{errors.password.message}</span>
                    </div>
                  )}

                  {/* Confirm password field */}
                  <div className="login-v2-field-wrap login-v2-pass-wrap">
                    <IonIcon icon={lockClosedOutline} className="login-v2-field-prefix-icon" />
                    <div className="login-v2-pass-content">
                      <Controller
                        name="passwordConfirmation"
                        control={control}
                        defaultValue=""
                        render={({ field: { onChange } }) => (
                          <IonInput
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="Confirm new password"
                            onIonChange={onChange}
                            autocomplete="new-password"
                          />
                        )}
                        rules={{ required: true }}
                      />
                    </div>
                    <div className="login-v2-eye-btn" onClick={() => setShowConfirmPassword((v) => !v)}>
                      <IonIcon icon={showConfirmPassword ? eyeOffOutline : eyeOutline} />
                    </div>
                  </div>
                  {errors.passwordConfirmation?.message && (
                    <div className="login-v2-field-error">
                      <IonIcon icon={warningOutline} />
                      <span>{errors.passwordConfirmation.message}</span>
                    </div>
                  )}

                  <IonButton type="submit" expand="block" className="login-v2-submit-btn">
                    Reset Password
                  </IonButton>
                </form>
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};
